import { DocumentNode } from '@apollo/client';
import { Language } from '@coolblue-development/constants';
import { InstallationServiceData } from '@coolblue-development/next-installation-services';
import type { ClientPropositionProps } from '@coolblue-development/next-propositions/client';
import type {
  Collection,
  ResponseCollection
} from '@features/product/components/Collection/Collection.types';
import { ConfiguratorUseCaseDetails } from '@features/product/components/Configurator/types';
import { DistinctProperty, EnergyLabelType, Product, Reviews } from '@features/product/typings';
import { Insurance } from '@features/product/typings';

import { StoreAvailabilitiesQueryResult } from '../api/queries/productStoreAvailabilitiesQuery';

export type ProductFragment = {
  document: DocumentNode;
  name: string;
};

export type ProductPageRepositoryProps = {
  productId: number;
  authToken?: string | undefined;
  dimensions?: ProductPageLayoutVisitDimensions;
};

export type ProductPageLayoutVisitDimensions = {
  device: string;
  customerType: string;
  trafficSource: string;
  channel?: string;
};

export type Device = 'desktop' | 'mobile' | 'tablet' | 'unknown';
export type CustomerType = 'consumer' | 'business_consumer' | 'b2b';
export type TrafficSource = 'search' | 'shopping' | 'price-comparison' | 'other';

export type ProductReviews = {
  id: string;
  authorName: string;
  title: string;
  text: string;
  pros: string[];
  cons: string[];
  createdAt: string;
  translatedFrom: 'nl' | 'en' | 'de' | null;
  ratingAverage: number;
  isExpertReview: boolean;
  images: { id: string }[];
};

/**
 * temporary type that fully duplicates all Props that are expected by next-review package ReviewCard component
 */
export type PackageReview = {
  id: number;
  ratingAverage: number;
  title: string;
  text: string;
  pros: string[];
  cons: string[];
  authorName: string;
  createdAt: string;
  translatedFrom: 'nl' | 'en' | 'de' | null;
  votes: {
    up: number;
    down: number;
  };
  productFullName: string;
  language: Language;
  discriminantValue: string;
  images: string[];
  isDrawer: boolean;
  expertReview?: boolean;
  productNameWrittenFor?: string;
  isTranslated?: boolean;
  starsAverage?: number;
  onVoteAction?: null;
  onOverlayOpen?: null;
  onOverlayClose?: null;
};

type AlternativeDto = {
  product: ProductDto | null;
  distinctProperties: DistinctProperty[];
};

export type ProductDto = Omit<Product, 'reviewMetaData' | 'insurance' | 'alternatives'> & {
  alternatives: AlternativeDto[] | null;
  reviewMetaData: Omit<Reviews, 'images'> & {
    images: { id: string }[];
  };
  productContent: {
    mainText: string | null;
    prosAndCons: {
      pros: string[] | null;
      cons: string[] | null;
    };
  } | null;
  energyLabel: EnergyLabelType | null;
};

export type ProductResponseData = {
  product: ProductDto | null;
  customer?: Customer | null;
  collectionByProductId: ResponseCollection | null;
  servicesByProductId: InstallationServiceData[] | null;
  navigationByProductId: ProductNavigation | null;
  productReviews: ProductReviews[];
  deliveryPromiseMetaDataForProductIds: DeliveryPromiseMetaDataResponse | null;
};

export type DeliveryPromiseMetaDataResponse = {
  deliveryMethod: 'TIMESLOT' | 'NEXTDAY' | 'TIMESPAN';
};

export type DeliveryPromiseMethod = 'default' | 'timespan' | 'cbDelivers';
export type DeliveryPromise = {
  deliveryMethod: DeliveryPromiseMethod;
};

export type ProductPage = {
  product: Product | null;
  customer: Customer | null;
  hasAccessories: boolean;
  services: InstallationServiceData[] | null;
  productNavigation: ProductNavigation | null;
  productReviews: PackageReview[];
  collectionByProductId: Collection | null;
  deliveryPromise: DeliveryPromise | null;
};

export type PropositionTypes = {
  header: ClientPropositionProps['propositions'];
  footer: ClientPropositionProps['propositions'];
  product?: ClientPropositionProps['propositions'];
  subscriptionInformation?: ClientPropositionProps['propositions'];
};

export type ProductNavigation = {
  productId: string;
  breadcrumbs: ProductNavigationNode[];
  topNavigation: ProductNavigationNode[];
  relevantSegments: ProductNavigationNode[];
};

export enum ProductContentType {
  CategoryPage = 'Category page',
  ProductTypeSegmentPage = 'Product type segment page',
  ProductTypePage = 'Product type page',
  ProductCollectionPage = 'Product Collection Page',
  BrandProductTypePage = 'Brand product type page'
}

export type ProductNavigationNode = {
  id?: string;
  title: string;
  url: string;
  urlIndexable?: boolean;
  contentType: ProductContentType;
};

export type ProductStoreAvailabilities = {
  product: Product | null;
};

export type CustomerPreferences = {
  preferredStore: {
    shopOutletId: number | null;
  };
};

export type Customer = {
  preferences?: CustomerPreferences | null;
};

export interface ProductPageRepositoryInterface {
  getByProductId(args: ProductPageRepositoryProps): Promise<ProductPage | null>;

  getProductStoreAvailabilities(
    args: ProductPageRepositoryProps
  ): Promise<StoreAvailabilitiesQueryResult | null>;

  getProductTradeInBrands(
    args: ProductTradeInBrandsProps
  ): Promise<ProductTradeInResponseData | null>;

  getProductImages(args: ProductImagesProps): Promise<ProductImagesResponseData | null>;

  getConfiguratorDetails(
    args: ConfiguratorRepositoryProps
  ): Promise<ConfiguratorUseCaseDetails['slots']>;

  getInsurance(args: { productId: string }): Promise<Insurance | null>;

  getPropositions(args: { productId: number }): Promise<PropositionTypes | null>;
}

export type ProductTradeInBrandsProps = number[];

export type ProductTradeInResponseData = {
  [key: string]: {
    name: string;
    image: {
      id: string;
    };
  };
};

export type ProductImagesProps = number[];

export type ProductImagesResponseData = {
  [key: string]: {
    primaryImage: {
      id: number;
      collectionName: string;
    };
  };
} | null;

export type ConfiguratorRepositoryProps = {
  productId: number;
  useCaseId: string;
};

export type ConfiguratorResponseData = {
  product: { configurator: Configurator | null } | null;
};

type ProductConfiguratorSlotQuantitySelector = {
  defaultQuantity: number;
  isEnabled: boolean;
};

export type ConfiguratorProductSlot = {
  defaultProduct: Product;
  description: string;
  discountPercentage: number;
  products: Product[];
  quantitySelector: ProductConfiguratorSlotQuantitySelector;
};

type ConfiguratorUseCase = {
  id: string;
  name: string;
  slots: ConfiguratorProductSlot[];
};

type Configurator = {
  useCases: ConfiguratorUseCase[];
};
